import React from 'react'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import Modal, {
  CloseButton,
  getBodyMaxHeight,
  PrevButton
} from '@jarvis.oddle/core/components/Modal'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import { shallowEqual } from 'src/utils/object'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import StoreViewer from './StoreViewer'
import StorePicker from './StorePicker'
import { colorConstants } from '@jarvis.oddle/core/theme/default'
import Loading from '@jarvis.oddle/core/components/Loading'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import {
  fetchAllStoresAction,
  fetchStoreInfoAction,
  selectStore
} from 'src/redux/stores'
import { syncPickupLocationAction } from 'src/redux/order/action'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import { selectDetailMenuSite } from 'src/redux/meta'

export const slides = {
  STORE_PICKER: 'STORE_PICKER',
  STORE_VIEWER: 'STORE_VIEWER'
}

const SubmitButton = () => {
  const { t } = React.useContext(TranslationContext)
  const form = useFormContext()
  const { values } = form
  return (
    <Form.SubmitButton fullWidth disabled={!values.selectedStoreId}>
      {t('Continue with Selection')}
    </Form.SubmitButton>
  )
}

const ModalBody = styled(Modal.Body)`
  ${breakpoint('desktop')`
    height: ${getBodyMaxHeight};
    max-height: 1024px;
  `}
`

const LoadingContainer = styled(ModalBody)`
  justify-content: center;
`

const PickupStoreSelectionModal = React.memo(
  ({
    modal
  }: {
    modal: {
      show: boolean
      onClose: () => void
      onClosed: () => void
    }
  }) => {
    const dispatch = useAppDispatch()
    const { t } = React.useContext(TranslationContext)
    const [slide, setSlide] = React.useState(slides.STORE_PICKER)
    const [userAddress, setUserAddress] = React.useState(undefined)
    const storeOrder = useAppSelector(selectOrder).storeOrder
    const menuId = useAppSelector(selectDetailMenuSite)?.menu?.id
    const availableStores = useAppSelector(selectStore)

    const selectedStoreId =
      storeOrder?.data?.storeId ||
      (availableStores?.data?.length > 0 && availableStores.data[0].id)

    const initialValues = React.useMemo(
      () => ({
        selectedStoreId,
        userAddress
      }),
      [selectedStoreId, userAddress]
    )

    // Always reload available store info for updated time slots.
    const onEnter = async () => {
      await dispatch(fetchAllStoresAction({ menuId }))
      await dispatch(fetchStoreInfoAction({ storeId: selectedStoreId }))
    }

    const onClosed = () => {
      setSlide(slides.STORE_PICKER)
      modal.onClosed()
    }

    const onViewMapClick = () => setSlide(slides.STORE_VIEWER)

    const onViewStoreClick = () => setSlide(slides.STORE_PICKER)

    const onSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true)
      const response = await dispatch(
        syncPickupLocationAction({
          storeId: values.selectedStoreId,
          lat: null,
          long: null,
          selectedDate: null
        })
      )
      if (
        response.meta.requestStatus === 'fulfilled' &&
        response.payload.payload.ok
      ) {
        pickUpDeliveryFlow.send('dateTimeSelection', {
          storeId: values.selectedStoreId
        })
      } else {
        modalController.showModal('ErrorModal', {
          error: t(response.payload.payload.error)
        })
      }
      setSubmitting(false)
    }

    const onFormChange = ({ values: prevValues }, { values: nextValues }) => {
      const {
        selectedStoreId: prevSelectedStoreId,
        userAddress: prevUserAddress
      } = prevValues || {}
      const {
        selectedStoreId: nextSelectedStoreId,
        userAddress: nextUserAddress
      } = nextValues || {}

      // preserve the user address value to the modal state,
      // since the modal is not destroyed after it is being closed,
      // the state will be preserved
      if (!shallowEqual(prevUserAddress || {}, nextUserAddress || {})) {
        setUserAddress(nextUserAddress)
      }

      if (prevSelectedStoreId !== nextSelectedStoreId) {
        dispatch(fetchStoreInfoAction({ storeId: nextSelectedStoreId }))
      }
    }

    return (
      <Modal
        {...modal}
        onEnter={onEnter}
        onClosed={onClosed}
        slideInAnimation='bottom'
      >
        <Form initialValues={initialValues} onSubmit={onSubmit}>
          <Form.Effect onChange={onFormChange} />
          <Modal.FlexContainer flexDirection='column'>
            <Modal.Header>
              <PrevButton onClick={pickUpDeliveryFlow.prev} />
              <H4 margin='none' bold>
                {t('Pickup')}
              </H4>
              <CloseButton onClick={modal.onClose} />
            </Modal.Header>
            {availableStores.status === 'pending' ? (
              <LoadingContainer>
                <Loading
                  color={colorConstants.white}
                  height={100}
                  width={100}
                />
              </LoadingContainer>
            ) : (
              <ModalBody>
                {slide === slides.STORE_PICKER ? (
                  <StorePicker
                    availableStores={availableStores.data}
                    onViewMapClick={onViewMapClick}
                  />
                ) : (
                  <StoreViewer
                    availableStores={availableStores.data}
                    onViewStoreClick={onViewStoreClick}
                  />
                )}
              </ModalBody>
            )}
            {availableStores.status !== 'pending' && (
              <Modal.Footer>
                <SubmitButton />
              </Modal.Footer>
            )}
          </Modal.FlexContainer>
        </Form>
      </Modal>
    )
  }
)

export default makeControlledModal(
  PickupStoreSelectionModal,
  'PickupStoreSelectionModal'
)
